<script setup lang="ts">
import { ref, computed, nextTick, watch, type FunctionalComponent } from 'vue';
import { RouterLink, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import {
    Dialog,
    DialogPanel,
    Menu,
    MenuButton,
    MenuItem,
    MenuItems,
    Popover,
    PopoverButton,
    PopoverPanel,
    TransitionChild,
    TransitionRoot,
} from '@headlessui/vue';
import {
    Bars3Icon,
    XMarkIcon,
    HomeIcon,
    DocumentTextIcon,
    PhotoIcon,
    RectangleGroupIcon,
    QuestionMarkCircleIcon,
    ListBulletIcon,
    ArrowUturnRightIcon,
    InboxIcon,
    GlobeAltIcon,
    UsersIcon,
    ArrowRightOnRectangleIcon,
    ArrowLeftOnRectangleIcon,
    UserCircleIcon,
    ExclamationTriangleIcon,
    ShoppingBagIcon,
    ShoppingCartIcon,
    TagIcon,
    ChevronUpDownIcon,
    Cog6ToothIcon,
    IdentificationIcon,
    UserGroupIcon,
    ClipboardDocumentListIcon,
    CalendarDaysIcon,
    ClockIcon,
    AdjustmentsHorizontalIcon,
    GiftIcon,
    ReceiptPercentIcon,
    TruckIcon,
    EnvelopeIcon,
    MapIcon,
    ChartBarIcon,
    NewspaperIcon,
    SunIcon,
    MoonIcon,
    ComputerDesktopIcon,
    ArrowTopRightOnSquareIcon,
    MagnifyingGlassIcon,
    ScaleIcon,
    CalculatorIcon,
    TableCellsIcon,
    StarIcon,
    KeyIcon,
} from '@heroicons/vue/24/outline';
import { useMeStore } from '@/stores/me';
import { useThemeStore, type ThemeMode } from '@/stores/theme';
import { selectWebsite } from '@/lib/websites';
import { refreshNavCounts, resetNavCounts, useNavCounts } from '@/lib/navCounts';
import { useLiveRefresh } from '@/lib/liveRefresh';
import SidebarContents from '@/components/SidebarContents.vue';

const { t } = useI18n();
const me = useMeStore();
const theme = useThemeStore();
const router = useRouter();
const navCounts = useNavCounts();

interface NavItem {
    name: string;
    to: { name: string };
    icon: FunctionalComponent;
    /** Count shown as a pill; hidden when 0 or null. */
    badge?: number | null;
    /** The count in words ("3 paid orders to ship"), for screen readers and the hover title. */
    badgeLabel?: string;
}

// Badge counts follow the active website: reset on a switch so the old
// tenant's number never shows under the new one, then fetch fresh.
watch(
    () => me.me?.activeWebsite?.id,
    () => {
        resetNavCounts();
        void refreshNavCounts();
    },
    { immediate: true },
);
// /me rides along: the sidebar and the website switcher are built from it,
// and another admin can add this user to a website or switch a module on
// the active one while this tab is open. Without it they only saw that
// after a reload. Every watcher on /me keys on the website id, so a reload
// that changed nothing resets nothing.
useLiveRefresh(() => Promise.all([refreshNavCounts(), me.load()]));

// Theme options for the user-menu segmented control.
const themeOptions = computed<Array<{ value: ThemeMode; label: string; icon: typeof SunIcon }>>(() => [
    { value: 'system', label: t('shell.theme.system'), icon: ComputerDesktopIcon },
    { value: 'light',  label: t('shell.theme.light'),  icon: SunIcon },
    { value: 'dark',   label: t('shell.theme.dark'),   icon: MoonIcon },
]);
const sidebarOpen = ref(false);
const exitingImpersonation = ref(false);

/**
 * Sidebar nav, grouped by concern so related surfaces sit together and the
 * admin-only block always lands last:
 *   1. (ungrouped) Dashboard — the home anchor.
 *   2. Content   — everything an editor authors: Pages, Articles, Landing
 *                  pages, Media, Menus.
 *   3. Customers — accounts and groups. Their own block because groups are
 *                  not only a commerce concept: they also gate who may see
 *                  a page or article ("Who can see this").
 *   4. Commerce: the shop modules, in the order an operator's day runs:
 *                  Orders (the daily queue), then what is sold (catalog),
 *                  what it costs (pricing), how it ships, and last the
 *                  newsletter.
 *   5. Insights  — inbound + analytics: Form submissions, Statistics.
 *   6. System: tenant + account admin: Websites, Users, the shop's VAT
 *              profile, and for super admins the tax table.
 *
 * Each item is gated exactly as before (module-enabled and/or admin); a
 * section only renders if at least one of its items survives the filter,
 * so editors never see an empty "System" header. SidebarContents draws the
 * label + divider per non-empty group.
 */
const nav = computed(() => {
    const sections = [
        {
            label: null,
            items: [
                { name: t('shell.nav.dashboard'), to: { name: 'dashboard' }, icon: HomeIcon },
            ],
        },
        {
            label: t('shell.nav.sections.content'),
            items: [
                { name: t('shell.nav.pages'), to: { name: 'pages' }, icon: DocumentTextIcon },
                // Blog module — editor-facing, like Pages.
                me.isModuleEnabled('blog')
                    && { name: t('shell.nav.articles'), to: { name: 'articles' }, icon: NewspaperIcon },
                // Landing-pages module. Bulk operational surface, but still
                // page-shaped content, so it sits with the rest of it.
                me.isModuleEnabled('landingpages')
                    && { name: t('shell.nav.landing'), to: { name: 'landing' }, icon: MapIcon },
                { name: t('shell.nav.media'), to: { name: 'media' }, icon: PhotoIcon },
                // Portfolio module — editor-facing. Collections are named
                // groups of photographs pointing at media folders, so it sits
                // next to Media rather than under Commerce.
                me.isModuleEnabled('portfolio')
                    && { name: t('shell.nav.portfolio'), to: { name: 'portfolio' }, icon: RectangleGroupIcon },
                // FAQ module — editor-facing. Questions and answers are
                // written copy, so it belongs with the other content, not
                // under a support or settings heading.
                me.isModuleEnabled('faq')
                    && { name: t('shell.nav.faq'), to: { name: 'faq' }, icon: QuestionMarkCircleIcon },
                { name: t('shell.nav.menus'), to: { name: 'menus' }, icon: ListBulletIcon },
                // Redirects — old URLs and where they go now. Content, not
                // settings: renaming or removing a page is what creates one.
                { name: t('shell.nav.redirects'), to: { name: 'redirects' }, icon: ArrowUturnRightIcon },
            ],
        },
        {
            // Customer module. Kept out of Commerce: a group is both a shop
            // audience (pricing, catalog visibility) and a content gate, so
            // the shop is not its only home.
            label: t('shell.nav.sections.customers'),
            items: [
                me.isModuleEnabled('customer')
                    && { name: t('shell.nav.customers'), to: { name: 'customers' }, icon: IdentificationIcon },
                // Groups sit next to the customer list because that is
                // where they are filled; what they unlock lives on the
                // content, under "Who can see this".
                me.isModuleEnabled('customer')
                    && { name: t('shell.nav.customerGroups'), to: { name: 'customer-groups' }, icon: UserGroupIcon },
            ],
        },
        {
            label: t('shell.nav.sections.commerce'),
            items: [
                // Vehicle stock module: a car dealer's stock, published to
                // the car platforms. First, because on a dealer site it is
                // what the admin is opened for; it needs no webshop.
                me.isModuleEnabled('vehicle_stock')
                    && { name: t('shell.nav.vehicleStock'), to: { name: 'vehicle-stock' }, icon: KeyIcon },
                // Checkout module. First: working through the orders that
                // came in is the job an operator opens the admin for.
                me.isModuleEnabled('checkout')
                    && {
                        name: t('shell.nav.orders'),
                        to: { name: 'orders' },
                        icon: ClipboardDocumentListIcon,
                        badge: navCounts.ordersToShip,
                        badgeLabel: t('shell.badges.ordersToShip', navCounts.ordersToShip ?? 0),
                    },
                // What is sold. Catalog module is per-website.
                ...(me.isModuleEnabled('catalog')
                    ? [
                        { name: t('shell.nav.products'), to: { name: 'products' }, icon: ShoppingBagIcon },
                        { name: t('shell.nav.categories'), to: { name: 'categories' }, icon: TagIcon },
                        // The shop-wide option taxonomy (Size, Colour,
                        // Amount). Which of it a product uses is chosen on
                        // the product; this is the shared list behind that.
                        { name: t('shell.nav.productOptions'), to: { name: 'product-options' }, icon: AdjustmentsHorizontalIcon },
                    ]
                    : []),
                // What it costs, from automatic to customer-entered.
                // Catalog-discount module: sale / volume pricing on the
                // catalog, not a code the customer enters. Right after the
                // catalog because it is set per product or category.
                me.isModuleEnabled('catalog_discount')
                    && { name: t('shell.nav.catalogDiscounts'), to: { name: 'catalog-discounts' }, icon: TagIcon },
                // Discount module. Voucher codes customers type at checkout.
                me.isModuleEnabled('discount')
                    && { name: t('shell.nav.vouchers'), to: { name: 'discounts' }, icon: ReceiptPercentIcon },
                // Gift card module. Sits next to Vouchers because that is
                // where an operator looks for "things that change what a
                // customer pays", even though a cadeaubon is money already
                // taken rather than a discount.
                me.isModuleEnabled('giftcard')
                    && { name: t('shell.nav.giftCards'), to: { name: 'gift-cards' }, icon: GiftIcon },
                // How it ships. Shipping module: carriers, rates, zones.
                me.isModuleEnabled('shipping')
                    && { name: t('shell.nav.shipping'), to: { name: 'shipping' }, icon: TruckIcon },
                // Holiday closure. Next to Shipping: it decides when the
                // orders coming in get shipped, and editors set it (the
                // checkout settings it is stored with are admin-only).
                me.isModuleEnabled('checkout')
                    && { name: t('shell.nav.holidayClosure'), to: { name: 'holiday-closure' }, icon: CalendarDaysIcon },
                // Delivery moments. Next to the holiday closure: both
                // decide when an order reaches the customer.
                me.isModuleEnabled('delivery_slot')
                    && { name: t('shell.nav.deliverySlots'), to: { name: 'delivery-slots' }, icon: ClockIcon },
                // Newsletter module. Last: marketing to past customers,
                // not part of taking and shipping an order. The campaigns
                // screen is the entry point (subscribers + settings link
                // from there).
                me.isModuleEnabled('newsletter')
                    && { name: t('shell.nav.newsletter'), to: { name: 'newsletter-campaigns' }, icon: EnvelopeIcon },
                // Review requests: also mail to past customers, after the
                // order is done, so it sits with the newsletter.
                me.isModuleEnabled('review_request')
                    && { name: t('shell.nav.reviewRequests'), to: { name: 'review-requests' }, icon: StarIcon },
                // Abandoned cart reminders: mail to shoppers about a cart
                // they left, so it sits with the other shop mail.
                me.isModuleEnabled('abandoned_cart')
                    && { name: t('shell.nav.abandonedCarts'), to: { name: 'abandoned-carts' }, icon: ShoppingCartIcon },
            ],
        },
        {
            label: t('shell.nav.sections.insights'),
            items: [
                {
                    name: t('shell.nav.formSubmissions'),
                    to: { name: 'form-submissions' },
                    icon: InboxIcon,
                    badge: navCounts.formSubmissionsUnread,
                    badgeLabel: t('shell.badges.unreadSubmissions', navCounts.formSubmissionsUnread),
                },
                // Statistics — visible to everyone with admin-SPA access
                // (editors included). The cms-api gate is relaxed to match;
                // see AnalyticsController.
                { name: t('shell.nav.statistics'), to: { name: 'statistics' }, icon: ChartBarIcon },
            ],
        },
        {
            label: t('shell.nav.sections.system'),
            items: me.isAdmin
                ? [
                    { name: t('shell.nav.websites'), to: { name: 'websites' }, icon: GlobeAltIcon },
                    { name: t('shell.nav.users'), to: { name: 'users' }, icon: UsersIcon },
                    // Tax module: the shop's VAT profile (fiscal facts,
                    // admin-only) and, for super admins, the tax table
                    // every shop is charged from.
                    me.isModuleEnabled('tax')
                        && { name: t('shell.nav.vat'), to: { name: 'tax' }, icon: ScaleIcon },
                    // SnelStart module: the issued invoices booked in the
                    // shop's own accounts, so it sits with the VAT profile.
                    me.isModuleEnabled('snelstart')
                        && { name: t('shell.nav.snelstart'), to: { name: 'snelstart' }, icon: CalculatorIcon },
                    me.isSuper
                        && { name: t('shell.nav.taxTable'), to: { name: 'tax-table' }, icon: TableCellsIcon },
                ]
                : [],
        },
    ];

    // Drop falsy (gated-out) items, then drop any section left empty.
    return sections
        .map(s => ({ ...s, items: s.items.filter(Boolean) as NavItem[] }))
        .filter(s => s.items.length > 0);
});

async function logout() {
    await me.logout();
    router.push({ name: 'login' });
}

async function exitImpersonation() {
    if (exitingImpersonation.value) return;
    exitingImpersonation.value = true;
    try {
        await me.stopImpersonating();
        router.push({ name: 'dashboard' });
    } finally {
        exitingImpersonation.value = false;
    }
}

/**
 * Website switcher list. Tenants with dozens of accessible websites made the
 * plain dropdown unusable (it ran off the viewport), so the panel filters on
 * name + code and only renders the first WEBSITE_LIST_LIMIT matches; the rest
 * are reachable by narrowing the query. The search box only appears once the
 * list is long enough to need it.
 */
const WEBSITE_LIST_LIMIT = 50;
const WEBSITE_SEARCH_THRESHOLD = 8;

const websiteQuery = ref('');
const websiteSearchInput = ref<HTMLInputElement | null>(null);

const websiteMatches = computed(() => {
    const all = me.me?.accessibleWebsites ?? [];
    const q = websiteQuery.value.trim().toLowerCase();
    if (q === '') return all;
    return all.filter(
        (w) => w.name.toLowerCase().includes(q) || w.code.toLowerCase().includes(q),
    );
});
const visibleWebsites = computed(() => websiteMatches.value.slice(0, WEBSITE_LIST_LIMIT));
const hiddenWebsiteCount = computed(() => websiteMatches.value.length - visibleWebsites.value.length);
const showWebsiteSearch = computed(
    () => (me.me?.accessibleWebsites?.length ?? 0) > WEBSITE_SEARCH_THRESHOLD,
);

function focusWebsiteSearch() {
    nextTick(() => websiteSearchInput.value?.focus());
}

async function switchWebsite(id: number) {
    websiteQuery.value = '';
    if (id === me.me?.activeWebsite?.id) return;
    await selectWebsite(id);
    me.loaded = false;
    await me.load();
    // Redirecting to the dashboard prevents staying on a per-website
    // resource (e.g. /pages/123) that may not exist on the new tenant.
    router.push({ name: 'dashboard' });
}
</script>

<template>
    <div class="flex h-full">
        <!-- Mobile sidebar -->
        <TransitionRoot as="template" :show="sidebarOpen">
            <Dialog class="relative z-50 lg:hidden" @close="sidebarOpen = false">
                <TransitionChild
                    as="template"
                    enter="transition-opacity ease-linear duration-300"
                    enter-from="opacity-0"
                    enter-to="opacity-100"
                    leave="transition-opacity ease-linear duration-300"
                    leave-from="opacity-100"
                    leave-to="opacity-0">
                    <div class="fixed inset-0 bg-slate-900/80" />
                </TransitionChild>

                <div class="fixed inset-0 flex">
                    <TransitionChild
                        as="template"
                        enter="transition ease-in-out duration-300 transform"
                        enter-from="-translate-x-full"
                        enter-to="translate-x-0"
                        leave="transition ease-in-out duration-300 transform"
                        leave-from="translate-x-0"
                        leave-to="-translate-x-full">
                        <DialogPanel class="relative mr-16 flex w-full max-w-xs flex-1">
                            <div class="absolute top-0 left-full flex w-16 justify-center pt-5">
                                <button type="button" class="-m-2.5 p-2.5" @click="sidebarOpen = false">
                                    <span class="sr-only">{{ $t('shell.sidebar.close') }}</span>
                                    <XMarkIcon class="h-6 w-6 text-white" />
                                </button>
                            </div>
                            <div class="flex grow flex-col gap-y-5 overflow-y-auto bg-white px-6 pb-4 dark:bg-slate-800">
                                <SidebarContents :nav="nav" />
                            </div>
                        </DialogPanel>
                    </TransitionChild>
                </div>
            </Dialog>
        </TransitionRoot>

        <!-- Static desktop sidebar -->
        <div class="hidden lg:flex lg:w-64 lg:flex-col lg:border-r lg:border-slate-200 lg:bg-white dark:lg:border-slate-800 dark:lg:bg-slate-800">
            <div class="flex grow flex-col gap-y-5 overflow-y-auto px-6 pb-4">
                <SidebarContents :nav="nav" />
            </div>
        </div>

        <!-- Main column -->
        <div class="flex min-w-0 flex-1 flex-col">
            <!-- Impersonation banner: shown for the whole session a super
                 spends acting as another user. The Exit button swaps back
                 to the super's own tokens. -->
            <div
                v-if="me.isImpersonating"
                class="flex items-center gap-x-3 bg-amber-500 px-4 py-2 text-sm text-amber-950 sm:px-6 lg:px-8">
                <ExclamationTriangleIcon class="h-5 w-5 shrink-0" />
                <span class="min-w-0 flex-1 truncate">
                    <i18n-t keypath="shell.impersonation.viewingAs" tag="span" scope="global">
                        <template #email><strong>{{ me.me?.email }}</strong></template>
                    </i18n-t>
                    <span class="hidden sm:inline"> {{ $t('shell.impersonation.by', { email: me.me?.impersonator?.email }) }}</span>
                </span>
                <button
                    type="button"
                    class="inline-flex shrink-0 items-center gap-x-1.5 rounded-md bg-amber-950/10 px-2.5 py-1 font-medium hover:bg-amber-950/20 disabled:opacity-60"
                    :disabled="exitingImpersonation"
                    @click="exitImpersonation">
                    <ArrowLeftOnRectangleIcon class="h-4 w-4" />
                    {{ exitingImpersonation ? $t('shell.impersonation.exiting') : $t('shell.impersonation.exit') }}
                </button>
            </div>

            <header class="flex h-14 items-center gap-x-4 border-b border-slate-200 bg-white px-4 dark:border-slate-800 dark:bg-slate-800 sm:px-6 lg:px-8">
                <button type="button" class="-m-2.5 p-2.5 text-slate-700 lg:hidden dark:text-slate-300" @click="sidebarOpen = true">
                    <span class="sr-only">{{ $t('shell.sidebar.open') }}</span>
                    <Bars3Icon class="h-6 w-6" />
                </button>

                <div class="flex flex-1 items-center justify-between gap-x-4">
                    <div class="flex items-center gap-x-1 text-sm">
                        <!-- Switcher: dropdown when the user has multiple
                             accessible websites; static label otherwise. -->
                        <Popover v-if="me.canSwitchWebsite && me.me?.activeWebsite" as="div" class="relative">
                            <PopoverButton
                                class="flex items-center gap-x-1.5 rounded-md px-2 py-1 text-sm text-slate-700 hover:bg-slate-100 focus:outline-none dark:text-slate-200 dark:hover:bg-slate-700"
                                @click="focusWebsiteSearch">
                                <span>{{ me.me.activeWebsite.name }}</span>
                                <span class="text-xs text-slate-400 dark:text-slate-500">{{ me.me.activeWebsite.code }}</span>
                                <ChevronUpDownIcon class="h-4 w-4 opacity-60" />
                            </PopoverButton>
                            <PopoverPanel
                                v-slot="{ close }"
                                class="absolute left-0 z-10 mt-2 w-72 origin-top-left rounded-md bg-white shadow-lg ring-1 ring-slate-900/5 focus:outline-none dark:bg-slate-800 dark:ring-slate-700">
                                <div v-if="showWebsiteSearch" class="relative border-b border-slate-200 dark:border-slate-700">
                                    <MagnifyingGlassIcon class="pointer-events-none absolute top-2.5 left-3 h-4 w-4 text-slate-400" />
                                    <input
                                        ref="websiteSearchInput"
                                        v-model="websiteQuery"
                                        type="search"
                                        :placeholder="$t('shell.websites.search')"
                                        class="w-full rounded-t-md border-0 bg-transparent py-2 pr-3 pl-9 text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none dark:text-slate-200"
                                        @keydown.esc.stop="websiteQuery === '' ? close() : (websiteQuery = '')" />
                                </div>
                                <div class="max-h-72 overflow-y-auto py-1">
                                    <p v-if="visibleWebsites.length === 0" class="px-3 py-2 text-xs text-slate-500">
                                        {{ $t('shell.websites.noMatch') }}
                                    </p>
                                    <button
                                        v-for="w in visibleWebsites"
                                        :key="w.id"
                                        type="button"
                                        class="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-slate-100 dark:hover:bg-slate-700"
                                        @click="close(); switchWebsite(w.id)">
                                        <span class="flex min-w-0 flex-col">
                                            <span class="truncate text-slate-700 dark:text-slate-200">{{ w.name }}</span>
                                            <span class="font-mono text-xs text-slate-400">{{ w.code }}</span>
                                        </span>
                                        <span v-if="me.me?.activeWebsite?.id === w.id" class="ml-2 shrink-0 text-xs text-blue-600 dark:text-blue-400">{{ $t('common.state.active') }}</span>
                                    </button>
                                </div>
                                <p
                                    v-if="hiddenWebsiteCount > 0"
                                    class="border-t border-slate-200 px-3 py-2 text-xs text-slate-500 dark:border-slate-700">
                                    {{ $t('shell.websites.more', { n: hiddenWebsiteCount }) }}
                                </p>
                            </PopoverPanel>
                        </Popover>
                        <span v-else-if="me.me?.activeWebsite" class="text-slate-500 dark:text-slate-400">
                            {{ me.me.activeWebsite.name }}
                            <span class="ml-2 text-xs text-slate-400 dark:text-slate-500">{{ me.me.activeWebsite.code }}</span>
                        </span>

                        <!-- Cog: jump straight to the active website's settings.
                             Admin-only, matching the website-edit route guard. -->
                        <RouterLink
                            v-if="me.isAdmin && me.me?.activeWebsite"
                            :to="{ name: 'website-edit', params: { id: me.me.activeWebsite.id } }"
                            class="rounded-md p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-700 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-slate-200"
                            :title="$t('shell.websites.settings')">
                            <span class="sr-only">{{ $t('shell.websites.settings') }}</span>
                            <Cog6ToothIcon class="h-4 w-4" />
                        </RouterLink>

                        <!-- Open the live site (canonical host) in a new tab.
                             Only shown when a canonical host is configured. -->
                        <a
                            v-if="me.me?.activeWebsite?.canonicalHost"
                            :href="`https://${me.me.activeWebsite.canonicalHost}/`"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="rounded-md p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-700 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-slate-200"
                            :title="$t('shell.websites.openLive', { host: me.me.activeWebsite.canonicalHost })">
                            <span class="sr-only">{{ $t('shell.websites.viewLive') }}</span>
                            <ArrowTopRightOnSquareIcon class="h-4 w-4" />
                        </a>
                    </div>

                    <Menu as="div" class="relative">
                        <MenuButton class="flex items-center gap-x-2 rounded-md px-2 py-1 text-sm text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-700">
                            <UserCircleIcon class="h-5 w-5" />
                            <span class="hidden sm:inline">{{ me.me?.email }}</span>
                        </MenuButton>
                        <MenuItems class="absolute right-0 z-10 mt-2 w-64 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-slate-900/5 focus:outline-none dark:bg-slate-800 dark:ring-slate-700">
                            <MenuItem v-slot="{ active }">
                                <RouterLink
                                    :to="{ name: 'profile' }"
                                    class="flex items-center gap-x-2 px-3 py-2 text-sm text-slate-700 dark:text-slate-200"
                                    :class="active ? 'bg-slate-100 dark:bg-slate-700' : ''">
                                    <UserCircleIcon class="h-4 w-4" />
                                    {{ $t('shell.userMenu.profile') }}
                                </RouterLink>
                            </MenuItem>

                            <!-- Theme: a 3-way segmented control. These are
                                 NOT MenuItems so picking one doesn't close the
                                 menu — the user can preview options live. -->
                            <div class="my-1 border-t border-slate-100 px-3 pt-2 pb-1 dark:border-slate-700">
                                <p class="mb-1 text-xs font-medium text-slate-400 dark:text-slate-500">{{ $t('shell.userMenu.theme') }}</p>
                                <div class="flex gap-1 rounded-md bg-slate-100 p-0.5 dark:bg-slate-900">
                                    <button
                                        v-for="opt in themeOptions"
                                        :key="opt.value"
                                        type="button"
                                        class="flex flex-1 items-center justify-center gap-x-1.5 rounded px-2 py-1.5 text-xs transition-colors"
                                        :class="theme.mode === opt.value
                                            ? 'bg-white text-slate-900 shadow-sm dark:bg-slate-700 dark:text-slate-100'
                                            : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'"
                                        :title="opt.label"
                                        @click="theme.setMode(opt.value)">
                                        <component :is="opt.icon" class="h-4 w-4 shrink-0" />
                                        <span>{{ opt.label }}</span>
                                    </button>
                                </div>
                            </div>

                            <MenuItem v-slot="{ active }">
                                <button
                                    class="flex w-full items-center gap-x-2 px-3 py-2 text-left text-sm text-slate-700 dark:text-slate-200"
                                    :class="active ? 'bg-slate-100 dark:bg-slate-700' : ''"
                                    @click="logout">
                                    <ArrowRightOnRectangleIcon class="h-4 w-4" />
                                    {{ $t('shell.userMenu.logout') }}
                                </button>
                            </MenuItem>
                        </MenuItems>
                    </Menu>
                </div>
            </header>

            <main class="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
                <slot />
            </main>
        </div>
    </div>
</template>
